import { useState } from 'react';
import { ModalPortal } from '@/components/ui/Modal/ModalPortal';
import { UserPlus, Mail, Pencil, Trash2, X, Check, Loader2 } from 'lucide-react';

interface TenantUser {
  id: string;
  userEmail: string;
  roleId: string;
  isActive: boolean;
  createdAt: string;
}

interface TenantUsersPanelProps {
  tenantId: string;
  users: TenantUser[];
  onUsersChange: () => void;
  userCount: number;
}

const ROLES = ['Administrador', 'Usuario', 'Contador'];

export default function TenantUsersPanel({
  tenantId,
  users,
  onUsersChange,
  userCount,
}: TenantUsersPanelProps) {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingEmail, setEditingEmail] = useState<string | null>(null);
  const [newRole, setNewRole] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [deletingEmail, setDeletingEmail] = useState<string | null>(null);

  const handleRoleUpdate = async (email: string) => {
    setIsUpdating(true);
    try {
      const res = await fetch(`/api/super-admin/tenants/${tenantId}/users/${email}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
        },
        body: JSON.stringify({ role: newRole }),
      });

      if (!res.ok) {
        throw new Error('Error al actualizar rol');
      }

      setEditingEmail(null);
      onUsersChange();
    } catch (error) {
      console.error('Error updating role:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteUser = async (email: string) => {
    if (!confirm(`¿Eliminar usuario ${email} del tenant?`)) return;

    setDeletingEmail(email);
    try {
      const res = await fetch(`/api/super-admin/tenants/${tenantId}/users/${email}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
        },
      });

      if (!res.ok) {
        throw new Error('Error al eliminar usuario');
      }

      onUsersChange();
    } catch (error) {
      console.error('Error deleting user:', error);
    } finally {
      setDeletingEmail(null);
    }
  };

  const handleAddUser = async (email: string, role: string) => {
    try {
      const res = await fetch(`/api/super-admin/tenants/${tenantId}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
        },
        body: JSON.stringify({ email, role }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Error al agregar usuario');
      }

      setIsAddModalOpen(false);
      onUsersChange();
    } catch (error) {
      throw error;
    }
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-lyrax-text">Usuarios</h2>
          <span className="px-2 py-1 bg-lyrax-primary/10 text-lyrax-primary text-xs font-medium rounded-full">
            {userCount}
          </span>
        </div>
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="btn-primary text-sm flex items-center gap-2"
        >
          <UserPlus className="w-4 h-4" />
          Agregar Usuario
        </button>
      </div>

      {users.length === 0 ? (
        <div className="text-center py-8">
          <div className="w-12 h-12 bg-lyrax-surface-hover rounded-full flex items-center justify-center mx-auto mb-3">
            <Mail className="w-6 h-6 text-lyrax-text-secondary" />
          </div>
          <p className="text-lyrax-text-secondary">No hay usuarios asignados</p>
          <p className="text-xs text-lyrax-text-secondary mt-1">
            Agrega usuarios para que accedan a este tenant
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {users.map((user) => (
            <div
              key={user.id}
              className="flex items-center justify-between p-3 bg-lyrax-surface-hover rounded-lg"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-lyrax-primary/10 rounded-full flex items-center justify-center">
                  <Mail className="w-4 h-4 text-lyrax-primary" />
                </div>
                <div>
                  <p className="font-medium text-lyrax-text">{user.userEmail}</p>
                  <p className="text-xs text-lyrax-text-secondary">
                    Agregado: {new Date(user.createdAt).toLocaleDateString('es-CL')}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {editingEmail === user.userEmail ? (
                  <div className="flex items-center gap-2">
                    <select
                      value={newRole}
                      onChange={(e) => setNewRole(e.target.value)}
                      className="input py-1 px-2 text-sm"
                    >
                      {ROLES.map((role) => (
                        <option key={role} value={role}>
                          {role}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => handleRoleUpdate(user.userEmail)}
                      disabled={isUpdating}
                      className="p-1 text-[#229E64] hover:bg-[#229E64]/10 rounded"
                    >
                      {isUpdating ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Check className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={() => setEditingEmail(null)}
                      className="p-1 text-lyrax-text-secondary hover:bg-lyrax-surface-hover rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${
                        user.isActive
                          ? 'bg-[#22C55E]/10 text-[#22C55E]'
                          : 'bg-lyrax-surface-hover text-lyrax-text-secondary'
                      }`}
                    >
                      {user.roleId}
                    </span>
                    <button
                      onClick={() => {
                        setEditingEmail(user.userEmail);
                        setNewRole(user.roleId);
                      }}
                      className="p-1 text-lyrax-text-secondary hover:text-lyrax-primary hover:bg-lyrax-surface-hover rounded transition-colors"
                      title="Editar rol"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteUser(user.userEmail)}
                      disabled={deletingEmail === user.userEmail}
                      className="p-1 text-lyrax-text-secondary hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                      title="Eliminar usuario"
                    >
                      {deletingEmail === user.userEmail ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {isAddModalOpen && (
        <AddUserModal
          onClose={() => setIsAddModalOpen(false)}
          onAdd={handleAddUser}
          existingEmails={users.map((u) => u.userEmail)}
        />
      )}
    </div>
  );
}

interface AddUserModalProps {
  onClose: () => void;
  onAdd: (email: string, role: string) => Promise<void>;
  existingEmails: string[];
}

function AddUserModal({ onClose, onAdd, existingEmails }: AddUserModalProps) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('Usuario');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (existingEmails.includes(email)) {
      setError('Este usuario ya está agregado al tenant');
      return;
    }

    setIsSubmitting(true);
    try {
      await onAdd(email, role);
      setEmail('');
      setRole('Usuario');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalPortal>
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-panel max-w-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-lyrax-text">Agregar Usuario</h2>
          <button onClick={onClose} className="icon-btn" aria-label="Cerrar">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Correo electrónico</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              placeholder="usuario@ejemplo.cl"
              required
            />
          </div>

          <div>
            <label className="label">Rol</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="input"
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary flex-1 flex items-center justify-center gap-2"
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {isSubmitting ? 'Agregando...' : 'Agregar'}
            </button>
          </div>
        </form>
      </div>
    </div>
    </ModalPortal>
  );
}
